// Gladiator Memory Game – match player photos. Add name to join the leaderboard.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { getStatsCollectionRoot } from '../utils/teamStatsConfig';
import gladiatorLogo from '../assets/images/gladiators-hero.png';

const getPlayerCards = () => {
  try {
    const req = require.context('../assets/players', false, /\.(jpg|jpeg|png)$/i);
    const list = req.keys().map((k) => {
      const name = k.replace(/^\.\//, '').replace(/\.(jpg|jpeg|png)$/i, '');
      const src = req(k);
      return { name, src: typeof src === 'string' ? src : (src && src.default) || '' };
    }).filter((p) => p.src);
    return list;
  } catch (e) {
    return [];
  }
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const PAIRS_PER_GAME = 6; // 12 cards total – mobile-friendly; different players each game

// Optional: adjust crop for specific player photos so faces/heads show better.
// Keys = player name (filename without extension). Value = CSS object-position (e.g. "center 20%" = more top, "center 50%" = center).
const CARD_OBJECT_POSITION = {
  // Example: 'Aiden': 'center 20%',
  // 'Alex S': 'center 35%',
};

export default function GladiatorMemoryGame({ teamId }) {
  const playerList = useMemo(() => getPlayerCards(), []);
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [moves, setMoves] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [gameComplete, setGameComplete] = useState(false);
  const [timeSeconds, setTimeSeconds] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [clockTick, setClockTick] = useState(0);

  const initGame = useCallback(() => {
    if (playerList.length === 0) return;
    const shuffled = shuffle([...playerList]);
    const count = Math.min(PAIRS_PER_GAME, shuffled.length);
    const selected = shuffled.slice(0, count);
    const paired = [];
    selected.forEach((p, i) => {
      paired.push({ id: `a-${i}`, playerName: p.name, src: p.src });
      paired.push({ id: `b-${i}`, playerName: p.name, src: p.src });
    });
    setCards(shuffle(paired).map((c, i) => ({ ...c, index: i, flipped: false, matched: false })));
    setFlipped([]);
    setMoves(0);
    setStartTime(null);
    setGameComplete(false);
    setTimeSeconds(0);
    setSubmitted(false);
    setPlayerName('');
    setSubmitError('');
  }, [playerList]);

  useEffect(() => {
    if (playerList.length > 0 && cards.length === 0) initGame();
  }, [playerList, cards.length, initGame]);

  const fetchLeaderboard = useCallback(async () => {
    if (!teamId || !db) return;
    setLeaderboardLoading(true);
    try {
      const col = collection(db, `${getStatsCollectionRoot()}/${teamId}/memoryLeaderboard`);
      const q = query(col, orderBy('moves', 'asc'), limit(20));
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => a.moves - b.moves || (a.timeSeconds || 0) - (b.timeSeconds || 0));
      setLeaderboard(list.slice(0, 10));
    } catch (e) {
      console.error('Leaderboard fetch failed', e);
    } finally {
      setLeaderboardLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  useEffect(() => {
    if (!startTime || gameComplete) return;
    const id = setInterval(() => setClockTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [startTime, gameComplete]);

  const handleCardClick = (index) => {
    if (cards[index].matched || cards[index].flipped) return;
    if (flipped.length === 2) return;

    if (!startTime) setStartTime(Date.now());

    const nextFlipped = [...flipped, index];
    const nextCards = cards.map((c, i) =>
      i === index ? { ...c, flipped: true } : c
    );
    setCards(nextCards);
    setFlipped(nextFlipped);

    if (nextFlipped.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = nextFlipped;
      const nameA = nextCards[a].playerName;
      const nameB = nextCards[b].playerName;
      if (nameA === nameB) {
        setCards((prev) =>
          prev.map((c, i) =>
            i === a || i === b ? { ...c, matched: true } : c
          )
        );
        setFlipped([]);
      } else {
        setTimeout(() => {
          setCards((prev) =>
            prev.map((c, i) =>
              i === a || i === b ? { ...c, flipped: false } : c
            )
          );
          setFlipped([]);
        }, 700);
      }
    }
  };

  useEffect(() => {
    if (cards.length === 0) return;
    if (cards.every((c) => c.matched)) {
      const elapsed = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
      setTimeSeconds(elapsed);
      setGameComplete(true);
    }
  }, [cards, startTime]);

  const handleSubmitName = async (e) => {
    e.preventDefault();
    const name = (playerName || '').trim();
    if (!name) {
      setSubmitError('Enter your name');
      return;
    }
    if (!teamId || !db) {
      setSubmitError('Unable to save');
      return;
    }
    setSubmitError('');
    try {
      await addDoc(collection(db, `${getStatsCollectionRoot()}/${teamId}/memoryLeaderboard`), {
        name,
        moves,
        timeSeconds,
        createdAt: new Date().toISOString(),
      });
      setSubmitted(true);
      fetchLeaderboard();
    } catch (err) {
      console.error('Submit failed', err);
      setSubmitError('Could not save. Try again.');
    }
  };

  if (playerList.length === 0) {
    return (
      <div className="card card-glad mb-4">
        <div className="card-body text-center py-5">
          <p className="text-muted-brand mb-0">No player photos available for the memory game yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card card-glad mb-4" style={{ overflow: 'hidden' }}>
      <div className="card-body">
        <h2 className="h5 mb-2" style={{ color: 'var(--brand-gold)', fontWeight: '700' }}>
          Gladiator Memory
        </h2>
        <p className="text-muted-brand mb-3" style={{ fontSize: '1.1rem' }}>
          Match the Gladiator photos. Each game shows {PAIRS_PER_GAME} random players—play again to see different Gladiators. Complete the game in the fewest moves to get to the top of the leaderboard.
        </p>

        {!gameComplete ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="small text-muted-brand">Moves: <strong style={{ color: '#e6e8ec' }}>{moves}</strong></span>
              {startTime && (
                <span className="small text-muted-brand">
                  Time: <strong style={{ color: '#e6e8ec' }}>{Math.floor((Date.now() - startTime) / 1000)}s</strong>
                </span>
              )}
              <button type="button" className="btn btn-sm btn-neutral" onClick={initGame}>
                New game
              </button>
            </div>
            <div
              className="d-flex flex-wrap justify-content-center gap-2"
              style={{ gap: '0.5rem', maxWidth: '100%' }}
            >
              {cards.map((card, index) => (
                <button
                  key={`${card.id}-${card.index}`}
                  type="button"
                  onClick={() => handleCardClick(index)}
                  className={`border-0 rounded p-0 memory-card-btn${card.matched ? ' memory-card-matched' : ''}`}
                  style={{
                    width: 'calc(25% - 0.25rem)',
                    minWidth: '100px',
                    maxWidth: '180px',
                    aspectRatio: '1',
                    perspective: '1000px',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    userSelect: 'none',
                    background: 'transparent',
                  }}
                >
                  <span
                    className="memory-card-inner"
                    style={{
                      transform: card.flipped || card.matched ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    }}
                  >
                    <span className="memory-card-face memory-card-front">
                      <img
                        src={gladiatorLogo}
                        alt="Gladiators"
                        style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.9, padding: '15%', boxSizing: 'border-box' }}
                        draggable={false}
                      />
                    </span>
                    <span className="memory-card-face memory-card-back">
                      <img
                        src={card.src}
                        alt={card.playerName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: CARD_OBJECT_POSITION[card.playerName] ?? 'center 28%',
                        }}
                        draggable={false}
                      />
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center mb-4">
            <p className="mb-2" style={{ color: 'var(--brand-gold)', fontWeight: '700' }}>
              You did it in {moves} moves and {timeSeconds}s!
            </p>
            {!submitted ? (
              <form onSubmit={handleSubmitName} className="mb-3">
                <label className="small text-muted-brand d-block mb-2">Add your name to the Gladiator Leaderboard</label>
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Your name"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    style={{ maxWidth: '200px', background: '#1a2332', color: '#e6e8ec', border: '1px solid var(--brand-border)' }}
                  />
                  <button type="submit" className="btn btn-sm btn-teal">
                    Join leaderboard
                  </button>
                </div>
                {submitError && <p className="small text-danger mt-2 mb-0">{submitError}</p>}
              </form>
            ) : (
              <p className="small text-success mb-0">You’re on the leaderboard!</p>
            )}
            <button type="button" className="btn btn-sm btn-neutral mt-2" onClick={initGame}>
              Play again
            </button>
          </div>
        )}

        <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--brand-border)' }}>
          <h3 className="h6 mb-2" style={{ color: 'var(--brand-gold)' }}>Gladiator Leaderboard</h3>
          {leaderboardLoading ? (
            <p className="small text-muted-brand mb-0">Loading…</p>
          ) : leaderboard.length === 0 ? (
            <p className="small text-muted-brand mb-0">No scores yet. Be the first!</p>
          ) : (
            <div
              className="rounded overflow-hidden"
              style={{
                background: 'var(--brand-card)',
                border: '1px solid var(--brand-border)',
                fontSize: 'clamp(0.8rem, 2.5vw, 0.95rem)',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2rem 1fr minmax(3rem, auto) minmax(2.5rem, auto)',
                  gap: '0.5rem 0.75rem',
                  alignItems: 'center',
                  padding: '0.6rem 0.75rem',
                  borderBottom: '1px solid var(--brand-border)',
                  color: 'var(--brand-gold)',
                  fontWeight: '600',
                }}
              >
                <span>#</span>
                <span>Name</span>
                <span style={{ textAlign: 'right' }}>Moves</span>
                <span style={{ textAlign: 'right' }}>Time</span>
              </div>
              {leaderboard.map((row, i) => (
                <div
                  key={row.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2rem 1fr minmax(3rem, auto) minmax(2.5rem, auto)',
                    gap: '0.5rem 0.75rem',
                    alignItems: 'center',
                    padding: '0.5rem 0.75rem',
                    borderBottom: i < leaderboard.length - 1 ? '1px solid var(--brand-border)' : 'none',
                    color: '#e6e8ec',
                  }}
                >
                  <span className="text-muted-brand">{i + 1}</span>
                  <span style={{ fontWeight: '600', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
                  <span style={{ textAlign: 'right' }}>{row.moves}</span>
                  <span style={{ textAlign: 'right' }}>{row.timeSeconds}s</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
